import { ReactElement } from "react";
import { Stack, Typography } from "@altimateai/lego";
import { ArrowDownIcon, ArrowUpIcon } from "@assets/icons";
import { PremiumBanner } from "@/pages/teammate/dataMates/PremiumBanner";
import { Usage } from "@/pages/mcp/mcpSlice";
import { Loader } from "@/components/loader";

export function DataMatesStats({
  usage,
  isLoading,
  start_date,
  end_date,
}: {
  usage: Usage | undefined;
  isLoading: boolean;
  start_date: Date;
  end_date: Date;
}): ReactElement {
  const diffInDays = Math.floor(
    (end_date.getTime() - start_date.getTime()) / (1000 * 60 * 60 * 24)
  );
  const trendLabel =
    diffInDays === 1
      ? "from previous day"
      : diffInDays < 7
        ? `from ${diffInDays} days ago`
        : diffInDays < 30
          ? "from previous week"
          : "from previous months";
  const data = [
    {
      action: "Assists",
      premium: false,
      score: usage?.totals.assists ?? 0,
      trend: (usage?.totals.assists ?? 0) >= (usage?.previous_totals?.assists ?? 0) ? "up" : "down",
      trendValue:
        usage?.previous_totals?.assists === 0
          ? (usage?.totals?.assists ?? 0) > 0
            ? "100"
            : "0"
          : usage?.previous_totals?.assists
            ? Math.abs(
                (((usage?.totals?.assists ?? 0) - usage.previous_totals.assists) /
                  usage.previous_totals.assists) *
                  100
              ).toFixed(1)
            : "0",
    },
    {
      action: "Guardrails",
      premium: false,
      score: usage?.totals.guardrails ?? 0,
      trend:
        (usage?.totals.guardrails ?? 0) >= (usage?.previous_totals?.guardrails ?? 0)
          ? "up"
          : "down",
      trendValue:
        usage?.previous_totals?.guardrails === 0
          ? (usage?.totals.guardrails ?? 0) > 0
            ? "100"
            : "0"
          : usage?.previous_totals?.guardrails
            ? Math.abs(
                (((usage?.totals.guardrails ?? 0) - usage.previous_totals.guardrails) /
                  usage.previous_totals.guardrails) *
                  100
              ).toFixed(1)
            : "0",
    },
    {
      action: "LLM Costs",
      premium: true,
      score: 5321,
      trend: "up",
      trendValue: 0,
    },
  ];
  return (
    <div className="al-bg-white al-p-4 al-rounded-2xl al-grid al-grid-cols-1 md:al-grid-cols-3 al-gap-4">
      {data.map((item, index) => (
        <Stack
          gap="lg"
          direction="column"
          key={index}
          className="al-text-lg al-flex-1 al-cursor-pointer al-transition-all al-duration-200 al-bg-gradient-to-r al-from-white al-to-blue-100 al-p-4 al-rounded-2xl al-border al-border-blue-100"
          aria-label={`${item.action} stat card`}
        >
          <Typography id={`stat-label-${item.action.toLowerCase().replace(/\s+/g, "-")}`}>
            {item.action}
          </Typography>
          {item.premium ? (
            <PremiumBanner variant={"large"} />
          ) : (
            <p
              className="al-text-3xl"
              aria-labelledby={`stat-label-${item.action.toLowerCase().replace(/\s+/g, "-")}`}
              aria-label={`${item.action} value: ${item.score}`}
            >
              {isLoading ? <Loader /> : <>{item.score}</>}
            </p>
          )}
          <Stack align="end" className="al-flex-1">
            {item.trend === "up" ? (
              <Stack
                gap="md"
                className="al-text-green-500 al-text-sm"
                align="center"
                aria-label={`${item.action} trend: up ${item.trendValue}%`}
              >
                {item.trendValue ? (
                  <>
                    <ArrowUpIcon aria-hidden="true" />
                    <Typography variant="caption">
                      {item.trendValue}% {trendLabel}
                    </Typography>
                  </>
                ) : null}
              </Stack>
            ) : (
              <Stack
                gap="md"
                className="al-text-red-500 al-text-sm"
                align="center"
                aria-label={`${item.action} trend: down ${item.trendValue}%`}
              >
                <ArrowDownIcon aria-hidden="true" />
                <Typography variant="caption">
                  {item.trendValue}% {trendLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}
    </div>
  );
}
